/**
 * React Query hooks for pool settings operations
 */

import type { PoolSettings, UpdatePoolSettingsInput } from "@eoln/userpool/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";

const POOL_SETTINGS_QUERY_KEY = "poolSettings";

/**
 * Fetch pool settings
 */
export function usePoolSettings() {
  const { client } = useAuth();

  return useQuery<PoolSettings>({
    queryKey: [POOL_SETTINGS_QUERY_KEY],
    queryFn: async () => {
      if (!client) {
        throw new Error("Not authenticated");
      }
      return client.getPoolSettings();
    },
    enabled: !!client,
  });
}

/**
 * Update pool settings
 */
export function useUpdatePoolSettings() {
  const { client } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<PoolSettings, Error, UpdatePoolSettingsInput>({
    mutationFn: async (input) => {
      if (!client) {
        throw new Error("Not authenticated");
      }
      return client.updatePoolSettings(input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [POOL_SETTINGS_QUERY_KEY] });
    },
  });
}
